import type React from "react"
import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import styled from "styled-components"
import backgroundImage from "~/assets/arbeidsark-bg-illustration.svg"
import { Button, Form, FormField, Input, Label } from "~/components"
import { FullPageLoader } from "~/components/Loader"
import { Margin } from "~/ur-react-components"
import { useForm, useTranslate } from "~/ur-react-hooks"
import { useKeyDown, useToast } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { trpc } from "~/utils/trpc"
import { validateEmail } from "~/utils/validation"
import Toast from "../toasts/Toast"

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-image: url(${backgroundImage});

  ${(props) =>
    props.theme.media.mobile &&
    `
      width: 100%;
  `}
`

const Title = styled.h3`
  font-family: 'Work Sans';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 145%;
  margin-top: 1rem;
  margin-bottom: 1rem;
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 383px;

  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0px 8px 32px 0px rgba(27, 12, 55, 0.12);
`

const BottomSection = styled.div`
  text-align: center;
  font-size: 14px;
  margin-top: 2rem;
  margin-bottom: 1rem;
`

const BackToLoginLink = styled(Link)`
  display: block;
  color: ${(props) => props.theme.colors.primary};
  font-weight: bold;
`

const CompletedSection = styled.div`
  margin-top: 1rem;
`

type ResetPasswordProps = {}

export const ResetPassword: React.FC<ResetPasswordProps> = () => {
  const translations = useTranslate({
    login: "common.log-in",
    email: "common.email",
    password: "common.password",

    title: "forgotten-password.title",
    titleSuccess: "forgotten-password.title-success",

    goBack: "forgotten-password.go-back",
    success: "forgotten-password.success",
    successMessage: "forgotten-password.success-message",

    createUser: "login.create-user",
    forgottenPassword: "login.forgotten-password",
    error: {
      createUser: "login.create-user-error",
      emailTaken: "login.email-address-already-in-use",
      invalidEmail: "errors.invalid-email-address",
    },
  })

  const nav = useNavigate()
  const [completed, setCompleted] = useState(false)

  const {
    formValues: form,
    formErrors,
    formChangeHandler: handler,
    submitHandler: submit,
    formValid,
    formEdited,
  } = useForm({
    values: {
      email: "",
    },
    validators: {
      email: validateEmail(translations.error.invalidEmail),
    },
  })

  const handleEnter = useKeyDown("Enter", submit(handleSubmit))

  const { getToastInfo } = useErrorHandling()
  const toast = useToast()

  const { data: isLoggedIn, isLoading: isLoggedInLoading } =
    trpc.authentication.isLoggedIn.useQuery()

  const { mutate: resetPassword } =
    trpc.authentication.resetPasswordRequest.useMutation({
      onSuccess() {
        setCompleted(true)
      },
      onError(error) {
        const { type, message } = getToastInfo(error.message, "", "error")
        toast(type, message)
      },
    })

  async function handleSubmit() {
    if (!formValid) {
      return
    }

    resetPassword({ email: form.email })
  }

  useEffect(() => {
    if (isLoggedIn) nav("/")
  }, [isLoggedIn, nav])

  if (isLoggedInLoading) return <FullPageLoader />

  return (
    <Wrapper>
      <Card>
        <Title>
          {completed ? translations.titleSuccess : translations.title}
        </Title>

        {completed && (
          <CompletedSection>
            <Toast type="success" message={translations.success} width="100%" />
            <BottomSection>
              <Link to="/login">
                <Button width="100%">{translations.goBack}</Button>
              </Link>
            </BottomSection>
          </CompletedSection>
        )}
        {!completed && (
          <>
            <Form preventDefault onSubmit={submit(handleSubmit)}>
              <FormField margin="1rem">
                <Label>{translations.email}</Label>
                <Input
                  value={form.email}
                  autoFocus
                  error={formErrors.email}
                  width="100%"
                  disabled={isLoggedInLoading}
                  onKeyDown={handleEnter}
                  onChange={handler("email")}
                />
              </FormField>
              <Margin top="1.5rem">
                <Button width="100%" disabled={!formValid || !formEdited}>
                  {translations.title}
                </Button>
              </Margin>
            </Form>
            <BottomSection>
              <BackToLoginLink to="/login">
                {translations.goBack}
              </BackToLoginLink>
            </BottomSection>
          </>
        )}
      </Card>
    </Wrapper>
  )
}
